// The call window's screen: two fixed panes side by side, the CRM on paper at the left and the coach on the Line at
// the right (Allen, October 30-31, 2026). Every slot is a fixed-size reservation with a data-slot name; the same tree
// renders in every phase, a slot shows a quiet placeholder until its content exists, and nothing animates but the
// settle of a corrected transcript row (opacity only). The More sheet is always mounted and sits over the transcript,
// so opening it moves nothing. Presentational: CallWindowRoot supplies the data. Icons through ui/Icon.tsx only.
//
// The transcript (docs/design/CALL-POP-KRISP-BAR.md lane 2): one row per speaker turn, its locked words in full colour
// the moment they arrive; the unlocked tail of the speech in progress is written muted on the transcript's reserved last
// line under the list (one line, always there, ellipsis, outside the scrolling list so rows arriving never push it), so
// a final lands with no colour flip. When the final decode heard locked words differently (the ASR's refined
// correction), the row's words are replaced in place with a brief settle; the row keeps its place and its height and no
// other row moves (Allen, November 1, 2026: "accuracy is a thing still"). The list follows the newest line only while
// the rep is at the bottom; otherwise a fixed "Jump to latest" chip, reserved in the transcript's header row, takes them
// back. At the end of an answered call the wrap-up takes say-next's box. Asking lives in the question thread under the
// transcript (lane 3); the More sheet holds only the other quick actions and stops above the thread, so it never covers
// the field or its answers (Ctrl/Cmd+Shift+K reaches the field with the sheet open). No global key listener here.
import { cutAtWord, type CallContextCard, type CallSummary } from '@bcio-voice/contracts';
import type { CoachQaEntry } from '../../call/coach/coach.ts';
import { type KeyboardEvent, type UIEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ArrowDown, Ellipsis, MessageCircle, Quote, Smile, X } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { COACH_MORE_ACTIONS, COACH_PRIMARY_ACTIONS } from '../../call/coach/coach.ts';
import type { CallWindowPhase, CrmPaneModel, PendingLine, SayNextModel, Tone, TranscriptRow } from './callWindowModel.ts';
import { SAY_PLACEHOLDER, rowShown } from './callWindowModel.ts';
import { CRM_CSS, CrmPane, type CrmDispoModel } from './CrmPane.tsx';
import { QUESTION_THREAD_CSS, QuestionThread } from './QuestionThread.tsx';
import { TIP_FEEDBACK_CSS, TipFeedback } from './TipFeedback.tsx';
import { WRAPUP_CSS, WrapUp } from './WrapUp.tsx';

const CSS = `
/* --cw-min-h: the shortest window every slot fits in (the compact rules at the end; callWindowLaunch.ts
   CALL_WINDOW_MIN_HEIGHT); a shorter window scrolls the page, which the rep can scroll back. overflow: clip, never
   hidden: a clipped box cannot be scrolled by focus or a shortcut, so nothing on screen moves when a field below the
   fold takes focus. */
.cw { --cw-min-h: 560px; height: max(100dvh, var(--cw-min-h)); display: grid; grid-template-columns: clamp(280px, 32%, 360px) minmax(0, 1fr); overflow: clip; background: var(--canvas); color: var(--ink); min-width: 640px; }
/* Nothing animates; the one exception is the corrected row's settle (.cw-seg-settle, opacity only). */
.cw, .cw * { animation: none !important; transition: none !important; }
.cw p, .cw h1, .cw h2, .cw h3 { margin: 0; }
.cw-ellipsis { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.cw-ph { display: inline-block; height: 10px; border-radius: var(--radius-control); background: var(--sunken); vertical-align: middle; }
.cw-ph-60 { width: 60%; } .cw-ph-45 { width: 45%; } .cw-ph-35 { width: 35%; }
.cw-dash { display: inline-block; width: 12px; height: 0; border-top: 1px solid var(--ink-3); vertical-align: middle; }
/* Readable guidance (Allen, November 2, 2026: "the coach - a little too small"). Main guidance uses section type,
   with supporting lines at reading size. Reserve three main rows and two supporting rows before any tip arrives,
   so updates and the wrap-up keep their fixed places. Short windows tighten line spacing and unused chrome rather
   than shrinking the text; narrow windows recover horizontal padding so the full COACH_LINE_MAX still fits. */
.cw-coach { background: var(--surface); padding: var(--space-4); display: flex; flex-direction: column; gap: var(--space-3); min-height: 0; min-width: 0; overflow: clip; container-type: inline-size; container-name: cw-coach;
  --cw-say-1-leading: var(--leading-read); --cw-say-why-leading: var(--leading-section);
  --cw-say-top: var(--leading-min); --cw-say-1-h: calc(3 * var(--cw-say-1-leading)); --cw-say-why-h: calc(2 * var(--cw-say-why-leading)); --cw-say-caller-h: var(--leading-body);
  --cw-say-gap: var(--space-1);
  --cw-say-h: calc(var(--cw-say-top) + var(--cw-say-1-h) + 2 * var(--cw-say-why-h) + var(--cw-say-caller-h) + 4 * var(--cw-say-gap)); }
.cw-head { flex: none; height: 56px; display: grid; grid-template-rows: 22px 18px; row-gap: var(--space-1); padding-bottom: var(--space-3); border-bottom: 1px solid var(--rule); box-sizing: content-box; }
.cw-head-a { display: flex; align-items: center; gap: var(--space-3); min-width: 0; height: 22px; }
.cw-head-name { flex: 1 1 auto; font-size: var(--text-section); line-height: 22px; font-weight: var(--wght-strong); }
.cw-head-state { flex: none; width: 19ch; justify-content: flex-start; min-height: 22px; overflow: hidden; }
.cw-head-state .cw-state-word { overflow: hidden; text-overflow: ellipsis; }
.cw-head-timer { flex: none; width: 7ch; text-align: end; font-size: var(--text-section); line-height: 22px; font-variant-numeric: tabular-nums; color: var(--ink); }
.cw-head-sub { font-size: var(--text-meta); line-height: 18px; color: var(--ink-2); }
.cw-say { flex: none; height: var(--cw-say-h); display: flex; flex-direction: column; gap: var(--cw-say-gap); }
.cw-say[hidden] { display: none; }
.cw-say-top { flex: none; height: var(--cw-say-top); display: flex; justify-content: space-between; gap: var(--space-3); font-size: var(--text-min); line-height: var(--cw-say-top); color: var(--ink-3); }
.cw-say-label { flex: 1 1 0; min-width: 0; font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
/* Left-aligned in its half: right-aligned text would move whenever its length changes. */
.cw-say-status { flex: 1 1 0; min-width: 0; text-align: start; }
.cw-say-status.is-warn { color: var(--warn); }
/* Line 1: three reading-spaced lines hold the longest line the coach sends (contracts COACH_LINE_MAX) in the narrowest window
   the call window opens (720 px wide; the Chrome harness measures it); the clamp is only a last resort, and the title
   keeps the whole line. */
.cw-say-1 { flex: none; height: var(--cw-say-1-h); font-size: var(--text-section); line-height: var(--cw-say-1-leading); font-weight: var(--wght-medium); color: var(--brass); display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden; overflow-wrap: anywhere; border-inline-start: 3px solid var(--brass-fill); padding-inline-start: var(--space-3); }
/* The placeholder is drawn by CSS in the empty box, so a line arriving is new text, never text that moved. */
.cw-say-1:empty { border-inline-start-color: var(--rule-strong); }
.cw-say-1:empty::before { content: 'Your next line shows here.'; font-size: var(--text-section); letter-spacing: normal; font-weight: var(--wght-body); color: var(--ink-3); }
/* No line will come while the status line says why (Coach off or not available, off hours, the call is over): the box
   stays empty instead of promising one. */
.cw-say-1.is-off:empty::before { content: none; }
.cw-say-1.is-muted { color: var(--ink-2); border-inline-start-color: var(--rule-strong); }
/* A tip that no longer covers the latest speech stays in place, dimmed, until the next one replaces it. */
.cw-say-1.is-stale { color: var(--ink-2); border-inline-start-color: var(--rule-strong); }
/* Lines 2 and 3 (why, then the next question): two reading-size lines each, wrapping; clamped only as a last resort,
   with the title keeping the whole line. The Chrome harness verifies all COACH_LINE_MAX (96) characters fit. */
.cw-say-2, .cw-say-3 { flex: none; height: var(--cw-say-why-h); font-size: var(--text-read); line-height: var(--cw-say-why-leading); color: var(--ink-2); padding-inline-start: calc(3px + var(--space-3)); display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; overflow-wrap: anywhere; min-width: 0; }
.cw-say-2.is-stale, .cw-say-3.is-stale { color: var(--ink-3); }
.cw-caller { flex: none; height: var(--cw-say-caller-h); display: flex; gap: var(--space-2); align-items: baseline; line-height: var(--cw-say-caller-h); }
.cw-caller-label { flex: none; font-size: var(--text-min); color: var(--ink-3); font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
/* The quote mark is an icon before the line: a generated closing quote would move with the line's length. */
.cw-caller-q { flex: 1 1 auto; font-size: var(--text-body); font-style: italic; color: var(--ink-2); quotes: none; }
.cw-caller-mark { flex: none; color: var(--ink-3); align-self: center; display: inline-flex; }
.cw-caller-q:empty::before { content: 'Their last words show here.'; font-style: normal; color: var(--ink-3); }
.cw-caller-q:empty::after { content: none; }
.cw-actions { flex: none; height: 36px; display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto; gap: var(--space-2); }
.cw-actions .btn { min-width: 0; overflow: hidden; }
.cw-actions .btn[aria-busy='true'] { border-color: var(--brass); }
.cw-transcript-region { flex: 1 1 auto; min-height: 0; position: relative; display: grid; grid-template-rows: 18px minmax(0, 1fr) var(--leading-read) auto; row-gap: var(--space-2); border-top: 1px solid var(--rule); padding-top: var(--space-2); }
.cw-transcript-head { display: flex; gap: var(--space-3); font-size: var(--text-min); line-height: 18px; color: var(--ink-3); font-weight: var(--wght-strong); letter-spacing: var(--track-small); }
.cw-transcript-word { flex: 1 1 auto; min-width: 0; font-weight: var(--wght-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* Jump to latest: always laid out at the end of the header row, shown only while the rep has scrolled up. */
.cw-jump { flex: none; box-sizing: border-box; height: 18px; display: inline-flex; align-items: center; gap: var(--space-1); margin: 0; padding: 0 var(--space-2); border: 1px solid var(--rule-strong); border-radius: var(--radius-control); background: var(--surface); color: var(--ink-2); font: inherit; font-size: var(--text-min); line-height: 16px; font-weight: var(--wght-medium); white-space: nowrap; cursor: pointer; }
.cw-jump:hover { color: var(--ink); }
.cw-jump:focus-visible { outline: 2px solid var(--focus); outline-offset: 1px; }
.cw-jump.is-idle { visibility: hidden; }
/* Rows sit at the bottom, the newest right above the reserved last line, so the tail on that line continues the row
   just above it (review round 2). Done by scrolling, never by moving rows: a spacer one box high less one row comes
   first, rows only ever append below it, and the list stays scrolled to the bottom while the rep is there. A row arriving
   is a scroll, so nothing shifts. */
.cw-transcript { overflow-y: auto; scrollbar-gutter: stable; overscroll-behavior: contain; margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: var(--space-2); min-height: 0; }
.cw-transcript::before { content: ''; flex: none; height: max(0px, calc(100% - var(--leading-read) - var(--space-2))); }
.cw-transcript > li { flex: none; }
.cw-seg { display: grid; grid-template-columns: 9ch minmax(0, 1fr); gap: var(--space-2); font-size: var(--text-read); line-height: var(--leading-read); }
.cw-seg-who { font-size: var(--text-meta); line-height: var(--leading-read); color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cw-seg-text { overflow-wrap: anywhere; }
.cw-seg-caller .cw-seg-text { color: var(--ink); }
.cw-seg-agent .cw-seg-text, .cw-seg-other .cw-seg-text { color: var(--ink-2); }
.cw-seg-partial .cw-seg-text { color: var(--ink-3); }
/* A corrected row (Allen, November 1, 2026: "accuracy is a thing still"): when the final decode heard locked words
   differently, the row shows the final words in place. The words it showed before stay laid out, unseen, in the same
   cell (.cw-seg-fit). Held (nothing has joined the row since), the final words are drawn over that box and clipped to
   it (overflow: clip, as every reserved box here), so the row keeps its height and no row under it moves; final words
   that need more lines than the row has end on its last line at a word boundary in one ellipsis, the coach tip's mark
   (SegWords), and the title keeps them all. Once new words join, both lie in the cell and the row holds the longer,
   so it never shrinks and the newest words are never cut. */
.cw-seg-corrected { position: relative; }
.cw-seg-fit { grid-column: 2; grid-row: 1; visibility: hidden; overflow-wrap: anywhere; }
.cw-seg-fit + .cw-seg-text { grid-column: 2; grid-row: 1; }
.cw-seg-held > .cw-seg-fit + .cw-seg-text { position: absolute; inset: 0; overflow: clip; }
/* The settle: the replaced words come up from 55% to full strength over 600 ms, so the rep notices them change while
   nothing moves. Opacity only; none with reduced motion, and none for a row out of view (TranscriptBox finishes it). */
@keyframes cw-seg-settle { from { opacity: 0.55; } to { opacity: 1; } }
@media (prefers-reduced-motion: no-preference) {
  .cw .cw-transcript .cw-seg-settle { animation: cw-seg-settle 600ms ease-out !important; }
}
/* The reserved last line, under the list: one line high whether or not speech is in progress; a long tail ends in an
   ellipsis. Outside the scrolling list, so neither new rows nor scrolling ever move it. */
.cw-pending { margin: 0; padding: 0; list-style: none; height: var(--leading-read); overflow: hidden; min-width: 0; }
.cw-pending > .cw-seg { height: var(--leading-read); overflow: hidden; }
.cw-pending .cw-seg-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; overflow-wrap: normal; }
.cw-seg-gap, .cw-seg-placeholder { font-size: var(--text-meta); line-height: var(--leading-meta); color: var(--ink-3); }
/* Over the transcript only: it stops above the question thread (QUESTION_THREAD_CSS, 9rem; 123 px in a short window) and
   the row gap before it, so the field and its answers stay in view and usable while the sheet is open. */
.cw-more { position: absolute; inset: 0 0 calc(9rem + var(--space-2)) 0; z-index: 1; display: grid; grid-template-rows: minmax(0, 1fr) auto; gap: var(--space-3); padding: var(--space-3); background: var(--raised); border: 1px solid var(--rule-strong); border-radius: var(--radius-panel); box-shadow: var(--float-2); }
.cw-more[hidden] { display: none; }
/* The wrap-up's mount: in say-next's place (same height) once an answered call has ended; hidden, it holds no space. */
.cw-wrapup-mount { flex: none; }
.cw-wrapup-mount[hidden] { display: none; }
.cw-more-actions { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); grid-auto-rows: 36px; gap: var(--space-2); align-content: start; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.cw-more-actions .btn { min-width: 0; overflow: hidden; padding: 0 var(--space-2); }
.cw-more-foot { display: flex; justify-content: flex-end; }
/* A short window (a laptop's maximized browser opens one about 600 px tall): tighter padding and gaps, a question
   thread whose answer rows hold two lines, and a More sheet with 32 px rows, so the transcript keeps several lines and
   every slot keeps its reserved size. The rules depend on the window's size only, never on what the call shows. */
@media (max-height: 719px) {
  .cw-coach { padding: var(--space-2) var(--space-4); gap: var(--space-2); }
  .cw-head { padding-bottom: var(--space-2); }
  .cw-transcript-region { padding-top: var(--space-1); row-gap: var(--space-1); }
  .cw-more { inset: 0 0 calc(123px + var(--space-1)) 0; padding: var(--space-2); gap: var(--space-2); }
  .cw-more-actions { grid-auto-rows: 32px; }
  .cw-more-actions .btn { min-height: 0; height: 32px; }
}
/* Narrow pop-ups keep the same readable type and recover room from padding, not the CRM's minimum width. */
@media (max-width: 799px) {
  .cw-coach { padding-inline: var(--space-3); }
  .cw-say-1 { padding-inline-start: var(--space-2); }
  .cw-say-2, .cw-say-3 { padding-inline-start: calc(3px + var(--space-2)); }
}
/* Below 640 px (the window's minimum is 560), retain the same text sizes and number of rows with tighter leading.
   Recover unused header/pane space so the transcript still keeps three lines and the question thread its answer row.
   These reservations depend only on the viewport; a tip, hold or wrap-up cannot move the controls. */
@media (max-height: 639px) {
  .cw-coach { gap: var(--space-1); padding-block: var(--space-1); --cw-say-1-leading: var(--leading-section); --cw-say-why-leading: var(--leading-body); }
  .cw-head { height: calc(22px + var(--space-1) + 18px); padding-bottom: var(--space-1); }
  .cw-actions { height: 32px; }
  .cw-actions .btn { min-height: 0; height: 32px; }
}
`;

/** The window's CSS and each pane's own, in this order. */
const STYLES = CSS + CRM_CSS + QUESTION_THREAD_CSS + WRAPUP_CSS + TIP_FEEDBACK_CSS;

export function CallWindowStyles() {
  return <style href="sidetone-call-window" precedence="call-window">{STYLES}</style>;
}

/** The fixed slots, in document order. Every phase renders exactly these. */
export const CALL_WINDOW_SLOTS = [
  'crm', 'crm-title', 'crm-prov', 'crm-who', 'crm-body', 'crm-next', 'crm-details', 'crm-history', 'crm-dispo', 'crm-dispo-status', 'crm-open', 'crm-open-status',
  'coach', 'head', 'head-name', 'head-state', 'head-timer', 'head-sub',
  'say', 'say-label', 'say-status', 'tip-feedback', 'say-1', 'say-2', 'say-3', 'caller', 'wrapup',
  'actions', 'transcript-region', 'transcript-head', 'transcript-jump', 'transcript', 'transcript-pending', 'question-thread', 'more-sheet',
] as const;

/** Say-next's slots: the wrap-up takes their box (the same rect as 'say') at the end of an answered call. */
export const SAY_SLOTS = ['say', 'say-label', 'say-status', 'tip-feedback', 'say-1', 'say-2', 'say-3', 'caller'] as const;

export type Vote = 'up' | 'down';

export interface CallWindowViewProps {
  /** Stable per window: element ids. */
  idBase: string;
  phase: CallWindowPhase;
  head: { name: string; sub: string; state: string; tone: Tone; timer: string };
  say: SayNextModel;
  caller: string | null;
  /** The question in flight, if any: every quick action waits for it. */
  askBusy: string | null;
  /** Quick actions only while the call is connected. */
  canAsk: boolean;
  /** Why asking is off, for the question thread's reserved line; null while it works. */
  askOff: string | null;
  /** The call shown, once the window is bound to one. */
  callId: string | null;
  transcript: { word: string; rows: TranscriptRow[]; pending: PendingLine | null };
  /** Thumbs for the tip in say-next: the tip's feedback id (feedbackClient.ts tipFeedbackTargetId; null for a pinned
   * answer, the frozen lines or no tip) and this rep's vote. */
  tip: { targetId: string | null; voted: Vote | null };
  /** The wrap-up, shown in say-next's place only for a call that was answered and then ended. */
  wrapup: { show: boolean; summary: CallSummary | null; waiting: boolean; until?: string | null; stalled?: boolean };
  /** This rep's vote on the wrap-up shown. */
  summaryVote: Vote | null;
  /** This rep's vote per question-thread answer, by the answer's entry id. */
  answerVotes: Readonly<Record<string, Vote>>;
  crm: CrmPaneModel;
  /** The raw card the CRM pane shows (the call's own, else the last call's with this number), for its 4b slots. */
  crmCard: CallContextCard | null;
  /** The window's clock, for the activity log's dates. */
  now: number;
  crmOpenStatus: string;
  /** The disposition shown as chosen, the one being saved, and the slot's status line. */
  dispo: CrmDispoModel;
  more: { open: boolean; entries: readonly CoachQaEntry[] };
  onAsk: (question: string) => void;
  onMore: (open: boolean) => void;
  onOpenCrm: () => void;
  onDisposition: (id: string) => void;
  onTipVote?: (vote: Vote) => void;
  onSummaryVote?: (vote: Vote) => void;
  onAnswerVote?: (entryId: string, vote: Vote) => void;
}

const LAMP: Record<Tone, string> = { live: 'live', ring: 'ring', hold: 'hold', warn: 'warn', end: 'end', neutral: 'neutral' };
const TONE: Record<Tone, string> = { live: 'tone-live', ring: 'tone-live', hold: 'tone-hold', warn: 'tone-warn', end: 'tone-end', neutral: 'tone-neutral' };

const AT_BOTTOM_PX = 32;
const atBottomOf = (el: HTMLElement) => el.scrollHeight - el.scrollTop - el.clientHeight <= AT_BOTTOM_PX;

/**
 * The transcript's header row and its list. The newest line stays in view while the reader is at the bottom; once they
 * scroll up the box is left alone and the Jump to latest chip (reserved at the end of the header row) shows.
 */
function TranscriptBox({ word, rows, pending }: { word: string; rows: TranscriptRow[]; pending: PendingLine | null }) {
  const ref = useRef<HTMLOListElement>(null);
  const atBottom = useRef(true);
  const [away, setAway] = useState(false);
  // A turn with no locked words yet has no row: its speech so far is all on the pending line.
  const shown = rows.filter(rowShown);
  const signature = shown.map(r => (r.kind === 'seg' ? `${r.key}:${r.revision}:${r.text.length}:${r.corrections ?? 0}` : r.key)).join('|');
  // Each corrected row's count of corrections already handled; null until the list's first layout.
  const settled = useRef<Map<string, number> | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // A correction keeps every row's size, so at the bottom this sets the scroll it already has: no scroll change.
    if (atBottom.current) el.scrollTop = el.scrollHeight;
    else if (el.scrollHeight <= el.clientHeight) { atBottom.current = true; setAway(false); }
    // A correction for a row out of view is applied silently: the words change and no settle plays, so scrolling back
    // finds them at rest. Rows corrected before the list first laid out never settle either.
    const before = settled.current, now = new Map<string, number>();
    let box: DOMRect | null = null;
    shown.forEach((row, i) => {
      if (row.kind !== 'seg' || !row.corrections) return;
      now.set(row.key, row.corrections);
      if (before?.get(row.key) === row.corrections) return;
      const li = el.children[i];
      if (!li) return;
      box ??= el.getBoundingClientRect();
      const r = li.getBoundingClientRect();
      if (before && r.bottom > box.top && r.top < box.bottom) return;
      for (const a of li.querySelector<HTMLElement>('.cw-seg-settle')?.getAnimations?.() ?? []) a.finish();
    });
    settled.current = now;
  }, [signature]);
  // The rows sit at the bottom by scrolling (CSS: the spacer before them is one box high): a resized window keeps them
  // there while the rep is at the bottom.
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => { if (atBottom.current) el.scrollTop = el.scrollHeight; });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const onScroll = (e: UIEvent<HTMLOListElement>) => {
    const bottom = atBottomOf(e.currentTarget);
    atBottom.current = bottom;
    setAway(!bottom);
  };
  const jump = () => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottom.current = true;
    setAway(false);
    // The chip hides once used: focus goes to the transcript it brought into view.
    el.focus({ preventScroll: true });
  };
  return (
    <>
      <p className="cw-transcript-head" data-slot="transcript-head">
        <span>Transcript</span>
        <span className="cw-transcript-word">{word}</span>
        <button type="button" className={`cw-jump${away ? '' : ' is-idle'}`} data-slot="transcript-jump" onClick={jump} tabIndex={away ? undefined : -1} aria-hidden={away ? undefined : true}>
          <Icon icon={ArrowDown} size="1em" /> Jump to latest
        </button>
      </p>
      <ol ref={ref} className="cw-transcript" data-slot="transcript" role="log" aria-live="off" aria-label="Transcript" tabIndex={0} onScroll={onScroll}>
        {rows.length ? shown.map(row => row.kind === 'gap' ? (
          <li key={row.key} className="cw-seg-gap">{row.text}</li>
        ) : (
          <li key={row.key} className={`cw-seg cw-seg-${row.role}${row.corrections ? ' cw-seg-corrected' : ''}${row.held ? ' cw-seg-held' : ''}`} data-revision={row.revision} data-corrections={row.corrections}>
            <span className="cw-seg-who">{row.who}</span>
            {/* A corrected row keeps the box of the words it showed before; held, the final words are drawn over it. */}
            {row.fit !== undefined ? <span className="cw-seg-fit" aria-hidden="true">{row.fit}</span> : null}
            {/* Keyed by its corrections, so each correction starts the settle again. */}
            <SegWords key={row.corrections ?? 0} text={row.text} held={!!row.held} settle={!!row.corrections} />
          </li>
        )) : <li className="cw-seg-placeholder">The transcript shows here as people speak.</li>}
      </ol>
      <ul className="cw-pending" data-slot="transcript-pending" aria-label="Speech in progress">
        <PendingRow pending={pending} />
      </ul>
    </>
  );
}

/**
 * A transcript row's words. Held (a correction drew them over the box of the words the row showed), words that need
 * more lines than that box has end on its last line at a word boundary in one ellipsis, the mark the coach tip's trim
 * uses (contracts cutAtWord), and the title keeps them all (integration review: they were clipped bare at the line).
 * Measured after layout and before paint, and again when the row's width changes; the box itself never changes.
 */
function SegWords({ text, held, settle }: { text: string; held: boolean; settle: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [cut, setCut] = useState<{ text: string; shown: string } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!held || !el) return;
    const measure = () => {
      const shown = heldWords(el, text);
      setCut(c => (c?.text === text && c.shown === shown ? c : { text, shown }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    let width = el.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const now = el.getBoundingClientRect().width;
      if (Math.abs(now - width) > 0.5) { width = now; measure(); }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, held]);
  const shown = held && cut?.text === text ? cut.shown : text;
  return <span ref={ref} className={`cw-seg-text${settle ? ' cw-seg-settle' : ''}`} title={held ? text : undefined}>{shown}</span>;
}

/** The held words a box shows whole: text itself when it fits, else the longest cutAtWord of it that does, each laid
 * out unseen in a copy of the words' box at its width. */
function heldWords(el: HTMLElement, text: string): string {
  const box = el.getBoundingClientRect();
  const probe = document.createElement('span');
  probe.className = 'cw-seg-text';
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = `position: absolute; visibility: hidden; top: 0; left: 0; display: block; width: ${box.width}px; height: auto; overflow: visible; animation: none;`;
  el.parentElement!.appendChild(probe);
  const fits = (words: string) => { probe.textContent = words; return probe.getBoundingClientRect().height <= box.height + 0.5; };
  let shown = text;
  if (!fits(text)) {
    let lo = 1, hi = Array.from(text).length;
    while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (fits(cutAtWord(text, mid))) lo = mid; else hi = mid; }
    shown = cutAtWord(text, lo);
  }
  probe.remove();
  return shown;
}

/** The reserved last line: the unlocked tail, muted, named only when its turn is not the row just above. */
function PendingRow({ pending }: { pending: PendingLine | null }) {
  if (!pending) return <li className="cw-seg" aria-hidden="true"><span className="cw-seg-who" /><span className="cw-seg-text" /></li>;
  return (
    <li className={`cw-seg cw-seg-${pending.role} cw-seg-partial`} data-turn={pending.key}>
      <span className="cw-seg-who">{pending.named ? pending.who : ''}</span>
      <span className="cw-seg-text" title={pending.text}>{pending.text}<span className="visually-hidden"> (in progress)</span></span>
    </li>
  );
}

export function CallWindowView(props: CallWindowViewProps) {
  const { idBase, head, say, crm, more } = props;
  const moreId = `cw-more-${idBase}`;
  const moreButton = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(more.open);
  useEffect(() => {
    if (more.open && !wasOpen.current) sheet.current?.querySelector<HTMLButtonElement>('button')?.focus();
    if (!more.open && wasOpen.current && sheet.current?.contains(document.activeElement)) moreButton.current?.focus();
    wasOpen.current = more.open;
  }, [more.open]);
  const busy = props.askBusy !== null;
  const disabled = busy || !props.canAsk;
  const sheetKeys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); props.onMore(false); moreButton.current?.focus(); }
  };
  const sayClass = `cw-say-1${say.placeholder ? ' is-placeholder' : say.muted ? ' is-muted' : say.stale ? ' is-stale' : ''}${say.off ? ' is-off' : ''}`;
  const dim = say.stale && !say.muted ? ' is-stale' : '';
  const wrapup = props.wrapup;
  return (
    <div className="cw" data-phase={props.phase}>
      <CallWindowStyles />
      <CrmPane crm={crm} status={props.crmOpenStatus} dispo={props.dispo} onOpen={props.onOpenCrm} onDisposition={props.onDisposition} card={props.crmCard} now={props.now} />
      <section className="cw-coach surface-line" data-slot="coach" aria-label="Coach">
        <header className="cw-head" data-slot="head">
          <div className="cw-head-a">
            <h1 className="cw-head-name cw-ellipsis" data-slot="head-name" title={head.name || undefined}>
              {head.name || <span className="cw-ph cw-ph-45" aria-hidden="true" />}
            </h1>
            <span className={`chip chip-lamp cw-head-state ${TONE[head.tone]}`} data-slot="head-state" role="status" title={head.state}>
              <span className={`dot dot-${LAMP[head.tone]}`} aria-hidden="true" />
              <span className="cw-state-word">{head.state}</span>
            </span>
            <span className="cw-head-timer num" data-slot="head-timer" role="timer" aria-live="off" aria-label="Call time">{head.timer}</span>
          </div>
          <p className="cw-head-sub cw-ellipsis" data-slot="head-sub" title={head.sub || undefined}>
            {head.sub || <span className="cw-ph cw-ph-35" aria-hidden="true" />}
          </p>
        </header>
        <div className="cw-say" data-slot="say" hidden={wrapup.show}>
          <div className="cw-say-top">
            <span className="cw-say-label" data-slot="say-label">{say.label}</span>
            <span className={`cw-say-status cw-ellipsis${say.statusTone === 'warn' ? ' is-warn' : ''}`} data-slot="say-status" role="status">{say.status}</span>
            <TipFeedback target="tip" targetId={props.tip.targetId} voted={props.tip.voted} onVote={vote => props.onTipVote?.(vote)} />
          </div>
          <p className={sayClass} data-slot="say-1" aria-live="polite" aria-label={say.line1 || say.off ? undefined : SAY_PLACEHOLDER} title={say.line1 || undefined}>{say.line1}</p>
          <p className={`cw-say-2${dim}`} data-slot="say-2" title={say.line2 || undefined}>{say.line2}</p>
          <p className={`cw-say-3${dim}`} data-slot="say-3" title={say.line3 || undefined}>{say.line3}</p>
          <p className="cw-caller" data-slot="caller">
            <span className="cw-caller-label">Caller</span>
            <span className="cw-caller-mark"><Icon icon={Quote} size="0.9em" /></span>
            <q className="cw-caller-q cw-ellipsis" title={props.caller ?? undefined}>{props.caller ?? ''}</q>
          </p>
        </div>
        {/* In say-next's place, with the same reserved height, once an answered call has ended (wrapUpShown). */}
        <div className="cw-wrapup-mount" hidden={!wrapup.show}>
          <WrapUp summary={wrapup.summary} waiting={wrapup.waiting} until={wrapup.until ?? null} stalled={wrapup.stalled ?? false} onDisposition={props.onDisposition} onFeedback={vote => props.onSummaryVote?.(vote)}
            dispositions={crm.dispositions} chosen={props.dispo.selected} busy={props.dispo.busy} voted={props.summaryVote} />
        </div>
        <div className="cw-actions" data-slot="actions" role="group" aria-label="Quick coaching">
          <button type="button" className="btn" disabled={disabled} aria-busy={props.askBusy === COACH_PRIMARY_ACTIONS[0] || undefined} onClick={() => props.onAsk(COACH_PRIMARY_ACTIONS[0])}>
            <Icon icon={MessageCircle} /> {COACH_PRIMARY_ACTIONS[0]}
          </button>
          <button type="button" className="btn" disabled={disabled} aria-busy={props.askBusy === COACH_PRIMARY_ACTIONS[1] || undefined} onClick={() => props.onAsk(COACH_PRIMARY_ACTIONS[1])}>
            <Icon icon={Smile} /> {COACH_PRIMARY_ACTIONS[1]}
          </button>
          {/* aria-disabled, not disabled: same label, width and focusability; it does nothing until the call is connected. */}
          <button ref={moreButton} type="button" className="btn" aria-expanded={more.open} aria-controls={moreId} aria-disabled={!props.canAsk || undefined}
            onClick={() => { if (props.canAsk || more.open) props.onMore(!more.open); }}>
            <Icon icon={Ellipsis} /> More
          </button>
        </div>
        <div className="cw-transcript-region" data-slot="transcript-region">
          {/* Keyed by call: the next call starts at the bottom with the chip hidden. */}
          <TranscriptBox key={idBase} word={props.transcript.word} rows={props.transcript.rows} pending={props.transcript.pending} />
          <QuestionThread callId={props.callId} idBase={idBase} entries={more.entries} onAsk={props.onAsk} disabled={!props.canAsk} busy={busy} disabledReason={props.askOff}
            votes={props.answerVotes} onFeedback={(entryId, vote) => props.onAnswerVote?.(entryId, vote)} />
          <div ref={sheet} id={moreId} className="cw-more" data-slot="more-sheet" role="region" aria-label="More coaching" hidden={!more.open} onKeyDown={sheetKeys}>
            <div className="cw-more-actions" role="group" aria-label="More quick actions">
              {COACH_MORE_ACTIONS.map(q => (
                <button key={q} type="button" className="btn btn-small" disabled={disabled} aria-busy={props.askBusy === q || undefined} onClick={() => props.onAsk(q)} title={q}>{q}</button>
              ))}
            </div>
            <div className="cw-more-foot">
              <button type="button" className="btn btn-small btn-quiet" onClick={() => { props.onMore(false); moreButton.current?.focus(); }}>
                <Icon icon={X} /> Close
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
